import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import appointmentService from "../../../Services/appointmentService";
import doctorService from "../../../Services/doctorService";

const SectionLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-widest font-[600] text-[#2F6DB4] mb-3 mt-6 sm:mt-8 border-b border-gray-100 pb-2">
    {children}
  </p>
);

const InputField = ({ label, hint, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">
      {label}{" "}
      {hint && <span className="text-gray-400 font-[400]">{hint}</span>}
    </label>
    <input
      {...props}
      className="w-full px-3 py-[9px] text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-300 focus:ring-2 focus:ring-orange-100 transition-colors"
    />
  </div>
);

const TextAreaField = ({ label, hint, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">
      {label}{" "}
      {hint && <span className="text-gray-400 font-[400]">{hint}</span>}
    </label>
    <textarea
      {...props}
      rows={4}
      className="w-full px-3 py-[9px] text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-300 focus:ring-2 focus:ring-orange-100 transition-colors resize-none"
    />
  </div>
);

const CustomSelect = ({ label, value, onChange, name, options, hint }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="flex flex-col gap-1" ref={ref}>
      <label className="text-[12px] font-[500] text-gray-500">
        {label}{" "}
        {hint && <span className="text-gray-400 font-[400]">{hint}</span>}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className={`w-full px-3 py-[9px] text-[13px] text-left bg-white border rounded-lg outline-none transition-colors flex items-center justify-between
            hover:border-orange-200
            ${open ? "border-orange-300 ring-2 ring-orange-100" : "border-gray-200"}
            ${value ? "text-gray-800" : "text-gray-400"}`}
        >
          <span className="truncate pr-2">{value || `Select ${label}`}</span>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${open ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute z-20 mt-1 w-full max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-[200px] overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-3 text-[13px] text-gray-400 text-center">No options found.</div>
            ) : (
              options.map((opt) => {
                const val = typeof opt === "object" ? opt.value : opt;
                const label_ = typeof opt === "object" ? opt.label : opt;
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => {
                      onChange({ target: { name, value: val } });
                      setOpen(false);
                    }}
                    className={`w-full px-3 py-[9px] text-[13px] text-left transition-colors
                      hover:bg-gray-50 hover:text-orange-600
                      ${value === val ? "bg-orange-50 text-orange-600 font-[500]" : "text-gray-700"}`}
                  >
                    {label_}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EMPTY_FORM = {
  full_name: "",
  contact_number: "",
  doctor_assigned: "",
  checkup_date: new Date().toISOString().split("T")[0],
  checkup_time: "",
  notes: "",
};

const AddAppointment = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(true);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await doctorService.fetchAllDoctors();
        if (res.doctors) setDoctors(res.doctors);
      } catch (err) {
        toast.error("Failed to load doctors.");
      } finally {
        setDoctorsLoading(false);
      }
    };
    fetchDoctors();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!formData.full_name.trim())              e.full_name       = "Required";
    if (formData.contact_number.length !== 11)    e.contact_number  = "Must be 11 digits";
    if (!formData.doctor_assigned)                e.doctor_assigned = "Required";
    if (!formData.checkup_date)                   e.checkup_date    = "Required";
    if (!formData.checkup_time)                   e.checkup_time    = "Required";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the highlighted errors.");
      return;
    }
    setLoading(true);
    try {
      const data = await appointmentService.create(formData);
      if (data.responseCode === 200) {
        toast.success("Appointment booked successfully!");
        navigate("/appointment");
      } else {
        toast.error("Failed to book appointment. Please try again.");
      }
    } catch (err) {
      toast.error("Server error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const ErrMsg = ({ field }) =>
    errors[field] ? (
      <span className="text-[11px] text-red-400 mt-0.5">{errors[field]}</span>
    ) : null;

  return (
    <div className="p-3 sm:p-6">
      <div className="border border-gray-200 bg-white rounded-xl overflow-hidden shadow-sm">

        <div className="bg-[#215187] px-4 sm:px-6 py-4 sm:py-6">
          <h2 className="text-white text-[13px] sm:text-[14px] font-[500] uppercase tracking-widest">
            New Appointment
          </h2>
          <p className="text-[12px] text-gray-400 mt-0.5">
            Fill in the details below to book an appointment
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-4 sm:px-6 py-4 sm:py-6">

          <SectionLabel>Patient Information</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <InputField
                label="Patient Name *"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="e.g. Ali Hassan"
              />
              <ErrMsg field="full_name" />
            </div>

            <div>
              <InputField
                label="Phone Number *"
                name="contact_number"
                value={formData.contact_number}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    contact_number: e.target.value.replace(/\D/g, "").slice(0, 11),
                  }))
                }
                placeholder="e.g. 03001234567"
              />
              <ErrMsg field="contact_number" />
            </div>
          </div>

          <SectionLabel>Appointment Details</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <CustomSelect
                label={doctorsLoading ? "Assigned Doctor (Loading...)" : "Assigned Doctor *"}
                name="doctor_assigned"
                value={formData.doctor_assigned}
                onChange={handleChange}
                options={doctors.map((d) => ({
                  value: d.name,
                  label: d.name + (d.specialization ? " — " + d.specialization : ""),
                }))}
              />
              <ErrMsg field="doctor_assigned" />
            </div>

            <div>
              <InputField
                label="Date *"
                name="checkup_date"
                value={formData.checkup_date}
                onChange={handleChange}
                type="date"
              />
              <ErrMsg field="checkup_date" />
            </div>

            <div>
              <InputField
                label="Time *"
                name="checkup_time"
                value={formData.checkup_time}
                onChange={handleChange}
                type="time"
              />
              <ErrMsg field="checkup_time" />
            </div>

            <div className="col-span-1 sm:col-span-2 lg:col-span-3">
              <TextAreaField
                label="Notes"
                hint="(optional)"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any additional notes about the appointment…"
              />
            </div>
          </div>

          <div className="border-t border-gray-200 mt-8 pt-5 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors font-[500] text-center sm:text-left py-2 sm:py-0"
            >
              ← Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-[#2F6DB4] hover:bg-[#255A8C] disabled:bg-gray-400 transition-colors text-white text-[13px] font-[500] px-6 py-[10px] sm:py-[7px] rounded-lg w-full sm:w-auto"
            >
              {loading ? "Saving..." : "Book Appointment →"}
            </button>
          </div>

        </form>
      </div>

      <p className="text-center text-[12px] text-gray-400 mt-4">
        Fields marked with * are required.
      </p>
    </div>
  );
};

export default AddAppointment;